import React, { useEffect, useRef } from 'react';
import { format, isValid, parse } from 'date-fns';
import { FormContext, FormContextProvider, FormSchemaFields } from '@stormwater-software/lib-form';
import { UIControlType } from '@stormwater-software/lib-form-control-types';
import FormModal from '../../Shared/form/modal/FormModal';
import reportApi from '../../../utils/api/report';

// DateControl (lib-form) always emits/displays "MM-dd-yyyy" (DateControl.tsx:64),
// regardless of what format it was seeded with — never ISO. The backend's
// startDate/endDate query params are "yyyy-MM-dd", so the picked value has to be
// converted at the boundary, or every filtered download 400s.
const toIsoDate = (value?: string): string | undefined => {
  if (!value) return undefined;

  const parsed = parse(value, 'MM-dd-yyyy', new Date());

  return isValid(parsed) ? format(parsed, 'yyyy-MM-dd') : undefined;
};

// SW2-2351 — both optional: leaving either blank keeps that side unbounded, matching
// the report's prior all-time behavior (no dates picked = no change from today).
const SCHEMA = {
  startDate: {
    controlType: UIControlType.date,
    label: 'Start date',
    style: { flex: '0 1 50%' },
  },
  endDate: {
    controlType: UIControlType.date,
    label: 'End date',
    style: { flex: '0 1 50%' },
  },
};

/**
 * Own FormContextProvider + Consumer (pattern matched to FileRenameModal.tsx and the
 * majority of this repo's form modals) rather than a bare `useContext(FormContext)` —
 * FormContext has no ambient provider on this page, so reading it directly returns null
 * and crashes on the first `.set()` call. HubspotReportModal happens to get away with
 * `useContext` alone; it is the outlier here, not the pattern to copy.
 */
const InspectionCoverageReportModal = ({ setShowModal }: { setShowModal: Function }) => {
  // Cancel/Escape can unmount this modal (and its FormContextProvider) while a download
  // is still in flight (FormModal defaults dismissOnEsc=true; FormActions' Cancel button
  // has no isBusy guard). Without this, a request that resolves after that point still
  // fires the file download the user thought they'd cancelled — guard the side effect
  // that's actually user-visible, scoped to this file only (not FormActions/FormModal,
  // which are shared across the whole app and out of scope for this ticket).
  const mountedRef = useRef(true);

  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  return (
    <FormContextProvider initialValue={{}}>
      <FormContext.Consumer>
        {formContext => {
          const submit = async () => {
            const { startDate, endDate } = formContext.value;
            let objectUrl: string | undefined;

            try {
              const blob = await reportApi.downloadInspectionCoverage({
                startDate: toIsoDate(startDate),
                endDate: toIsoDate(endDate),
              });

              if (!mountedRef.current) return;

              objectUrl = window.URL.createObjectURL(blob);
              const a = document.createElement('a');

              a.href = objectUrl;
              a.download = `account_project_overview_${new Date()
                .toISOString()
                .split('T')[0]}.xlsx`;
              document.body.appendChild(a);
              a.click();
              a.remove();
            } catch (err) {
              // `responseType: 'blob'` means an error body also arrives as a Blob, so
              // FormActions' error handler (which reads err.response.data.message) finds
              // nothing and falls back to a generic message — parse it back to JSON so
              // the real server message (e.g. a rejected reversed date range) surfaces.
              if (err?.response?.data instanceof Blob) {
                try {
                  const parsed = JSON.parse(await err.response.data.text());

                  // A body of literal `null` (or any non-object) parses without throwing,
                  // but FormActions' handler does `data.message` with no null guard — only
                  // reassign when there's an object to actually read a message off of, or
                  // this is worse than the pre-fix Blob (which at least fell through to the
                  // generic error message instead of throwing).
                  if (parsed !== null && typeof parsed === 'object') {
                    err.response.data = parsed;
                  }
                } catch {
                  // leave as-is if the body isn't JSON
                }
              }

              // A gateway timeout reaches axios with no readable response (the 504 carries no
              // CORS headers), so FormActions would show axios' bare "Network Error".
              if (!err?.response || err.response.status === 504) {
                throw new Error(
                  'The report took too long to generate. Try a shorter date range, or try again later.',
                );
              }
              throw err;
            } finally {
              if (objectUrl) window.URL.revokeObjectURL(objectUrl);
            }

            setShowModal(false);
          };

          return (
            <FormModal
              onSubmit={submit}
              onCancel={() => setShowModal(false)}
              submitText='Download'
              modalProps={{
                title: 'Account Project Overview',
              }}
            >
              <section className='inspection-coverage-report-form'>
                <p className='inspection-coverage-report-form__hint'>
                  Leave both dates blank to export all-time, as before. When set,
                  only inspections submitted within the range are counted.
                </p>
                <FormSchemaFields
                  schema={SCHEMA}
                  onChange={formContext.set}
                  formData={formContext.value}
                  initialFormData={{}}
                />
              </section>
            </FormModal>
          );
        }}
      </FormContext.Consumer>
    </FormContextProvider>
  );
};

export default InspectionCoverageReportModal;
